import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { useSidePanelStore } from '#/shared/lib/side-panel'
import { STORAGE_KEYS } from '#/shared/lib/storage'

import { dropTabSelection } from './tab-selection-snapshots'
import { dropTabState } from './workspace-tab-state'

export interface WorkspaceTab {
  id: string
  /** Full in-app location (pathname + search) this tab currently shows. */
  href: string
  /** Last page title seen while this tab was active ('' until first visit). */
  title: string
  /** User-typed name (via inline rename). Wins over the auto `title` and is
   *  never overwritten by navigation; absent until the user renames. */
  customTitle?: string
  /** Whether the phone side panel is open in this workspace. The panel's
   *  content (chat/chatTo/phoneTab) rides in the href; open/closed lives in a
   *  store, so it's snapshotted here per workspace. */
  panelOpen: boolean
}

interface WorkspaceTabsState {
  tabs: WorkspaceTab[]
  activeTabId: string | null
  /** The tab that was active before this one — Ctrl+Tab's swap target. Not
   *  persisted: "where I just came from" means nothing after a reload. */
  previousTabId: string | null
  /** Open a new tab pointing at `href` and make it active. Navigation is the
   *  caller's job — the store never touches the router. */
  openTab: (href: string) => void
  /** Open a tab without leaving the current one — the ctrl/middle-click case,
   *  where a browser would put the page in a background tab. `title` seeds the
   *  label (the link's own text) until the page is visited and titles itself. */
  openTabInBackground: (href: string, title?: string) => void
  activateTab: (id: string) => void
  /** Close a tab. If it was active, the nearest right neighbour (else left)
   *  becomes active; the caller navigates to the new active tab's href. */
  closeTab: (id: string) => void
  /** Keep the active tab following router navigation, like a browser tab
   *  remembers where it was. Adopts the current location into a first tab when
   *  none exist yet (fresh session). */
  syncActiveHref: (href: string) => void
  setActiveTitle: (title: string) => void
  setActivePanelOpen: (panelOpen: boolean) => void
  /** Move tab `id` into the slot currently held by tab `targetId` (drag
   *  reorder). No-op if either is unknown or they're the same tab. */
  moveTab: (id: string, targetId: string) => void
  /** Set a user-chosen name on a tab. An empty string clears it, falling back
   *  to the auto page title again. */
  renameTab: (id: string, title: string) => void
}

function newTab(href: string): WorkspaceTab {
  return {
    id: crypto.randomUUID(),
    href,
    title: '',
    // A new workspace inherits the current panel state, the same way the chat
    // params ride into it via retainSearchParams.
    panelOpen: useSidePanelStore.getState().isOpen,
  }
}

/** Drop a tab and hand its slot to a neighbour. The last tab never closes. */
function withTabClosed(
  state: WorkspaceTabsState,
  id: string,
): Partial<WorkspaceTabsState> {
  const index = state.tabs.findIndex((tab) => tab.id === id)
  if (index === -1 || state.tabs.length === 1) return state
  const tabs = state.tabs.filter((tab) => tab.id !== id)
  const closedActive = state.activeTabId === id
  return {
    tabs,
    activeTabId: closedActive
      ? tabs[Math.min(index, tabs.length - 1)].id
      : state.activeTabId,
    // Nothing sensible to swap back to once either end of the pair is gone.
    previousTabId:
      closedActive || state.previousTabId === id ? null : state.previousTabId,
  }
}

export const useWorkspaceTabsStore = create<WorkspaceTabsState>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeTabId: null,
      previousTabId: null,

      openTab: (href) =>
        set((state) => {
          const tab = newTab(href)
          return {
            tabs: [...state.tabs, tab],
            activeTabId: tab.id,
            previousTabId: state.activeTabId,
          }
        }),

      openTabInBackground: (href, title = '') =>
        set((state) => ({ tabs: [...state.tabs, { ...newTab(href), title }] })),

      activateTab: (id) =>
        set((state) => {
          if (id === state.activeTabId) return state
          if (!state.tabs.some((tab) => tab.id === id)) return state
          return { activeTabId: id, previousTabId: state.activeTabId }
        }),

      closeTab: (id) => {
        set((state) => withTabClosed(state, id))
        // Only if the tab actually closed — the store keeps the last tab open,
        // and that one's state should survive the refused close.
        if (!get().tabs.some((tab) => tab.id === id)) {
          dropTabSelection(id)
          dropTabState(id)
        }
      },

      syncActiveHref: (href) =>
        set((state) => {
          const active = state.tabs.find((tab) => tab.id === state.activeTabId)
          if (!active) {
            const tab = newTab(href)
            return { tabs: [...state.tabs, tab], activeTabId: tab.id }
          }
          if (active.href === href) return state
          return {
            tabs: state.tabs.map((tab) =>
              tab.id === active.id ? { ...tab, href } : tab,
            ),
          }
        }),

      setActiveTitle: (title) =>
        set((state) => {
          const active = state.tabs.find((tab) => tab.id === state.activeTabId)
          if (!active || active.title === title) return state
          return {
            tabs: state.tabs.map((tab) =>
              tab.id === active.id ? { ...tab, title } : tab,
            ),
          }
        }),

      moveTab: (id, targetId) =>
        set((state) => {
          const from = state.tabs.findIndex((tab) => tab.id === id)
          const to = state.tabs.findIndex((tab) => tab.id === targetId)
          if (from === -1 || to === -1 || from === to) return state
          const tabs = [...state.tabs]
          const [moved] = tabs.splice(from, 1)
          tabs.splice(to, 0, moved)
          return { tabs }
        }),

      renameTab: (id, title) =>
        set((state) => ({
          tabs: state.tabs.map((tab) =>
            tab.id === id ? { ...tab, customTitle: title || undefined } : tab,
          ),
        })),

      setActivePanelOpen: (panelOpen) =>
        set((state) => {
          const active = state.tabs.find((tab) => tab.id === state.activeTabId)
          if (!active || active.panelOpen === panelOpen) return state
          return {
            tabs: state.tabs.map((tab) =>
              tab.id === active.id ? { ...tab, panelOpen } : tab,
            ),
          }
        }),
    }),
    {
      name: STORAGE_KEYS.workspaceTabs.key,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        tabs: state.tabs,
        activeTabId: state.activeTabId,
      }),
    },
  ),
)
